import { GetPayScheduleResponse } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/payroll/resources/payscheduleApi';
import useSWR from 'swr';

import { fetcher } from 'thera-swr/fetcher';
import { IW2PayrollInfo } from 'types';
import { globalFetcherSettings } from 'lib/constants';
import { useCompany } from 'context/company';
import { usePermissions } from './usePermissions';
import { useGetPaySchedules } from './useCheckSWR';
import { isAutopilotOn } from 'utils/paySchedule';

const apiBaseUrl = process.env.NEXT_PUBLIC_BACKEND_URL;

/**
 * Custom hook that combines payroll info with pay schedules
 * to provide comprehensive payroll data.
 * This hook merges data from:
 * - useSWR: Gets payroll information
 * - useGetPaySchedules: Gets pay schedule configurations
 * @returns Combined payroll data with loading states and error handling
 */
export default function useCompanyW2PayrollInfo() {
  const { isOnlyAPARManager } = usePermissions();
  const { company, isLoading: isCompanyLoading } = useCompany();
  const checkCompanyId = Boolean(company?.w2Payroll?.checkId);
  const companyID = company?.companyID;
  const path = `/companies/${companyID}/w2-payroll/info`;
  const {
    data: w2PayrollInfo,
    error,
    mutate,
    isLoading,
  } = useSWR<IW2PayrollInfo[]>(
    () => (companyID && checkCompanyId && !isOnlyAPARManager ? [path] : false),
    fetcher(apiBaseUrl + path),
    {
      errorRetryCount: 3, // Retry 3 times on error
      errorRetryInterval: 1000, // Retry interval in milliseconds (1 second in this case)
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        // You can customize retry behavior here if needed
        console.warn(`Retry attempt ${retryCount}`);
      },
      ...globalFetcherSettings,
    },
  );

  // Get pay schedules data
  const {
    w2PaySchedules: paySchedules,
    mutateW2PaySchedules,
    isPending: isPaySchedulesPending,
    error: paySchedulesError,
  } = useGetPaySchedules();

  const isPending = isCompanyLoading || (isLoading && !w2PayrollInfo) || isPaySchedulesPending;

  const w2PayrollInfoWithPaySchedule = w2PayrollInfo?.map((payrollInfo) => {
    const paySchedule = paySchedules?.find((paySchedule) => paySchedule.id === payrollInfo.payScheduleId);
    const isAutoPilotOn = isAutopilotOn(paySchedule);
    return {
      ...payrollInfo,
      paySchedule: paySchedule as GetPayScheduleResponse,
      isAutoPilotOn: isAutoPilotOn,
    };
  });

  const combinedMutate = async () => {
    await mutateW2PaySchedules();
    await mutate();
  };

  // Combine errors only if they exist
  const combinedError = (() => {
    if (error && paySchedulesError) {
      // Both hooks have errors
      return new Error(
        `Error fetching payroll data: ${error.message}; Pay schedules error: ${paySchedulesError.message}`,
      );
    } else if (error) {
      // Only payroll info has error
      return new Error(`Error fetching w2 payroll info: ${error.message}`);
    } else if (paySchedulesError) {
      // Only pay schedules has error
      return new Error(`Error fetching pay schedules: ${paySchedulesError.message}`);
    }
    // No errors
    return null;
  })();

  return {
    w2PayrollInfo: w2PayrollInfoWithPaySchedule,
    mutatePayrollInfo: combinedMutate,
    // Only show loading for initial load, not background revalidation
    isPending,
    error: combinedError,
  };
}
